import type { UserBetHistoryResponse } from "../../types/api.generated";

export interface BetCreatePayload {
  participationId: string;
  stakedPoints: number;
}

export type BetHistoryPeriod = "weekly" | "monthly" | "all";

export interface BetHistoryQueryParams {
  period?: BetHistoryPeriod;
}

type ApiEnvelope<T> = T | { data: T };

const getAuthHeaders = () => {
  const token = sessionStorage.getItem("tokenUser");
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
};

const getBaseUrl = () => (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const buildBetUrl = (path: string, params: BetHistoryQueryParams = {}) => {
  const base = getBaseUrl();
  const url = new URL(`${base}${path}`, window.location.origin);

  if (params.period) url.searchParams.set("period", params.period);

  return base ? `${base}${path}${url.search}` : `${path}${url.search}`;
};

export const create = async (params: BetCreatePayload): Promise<any> => {
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/bets`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(params)
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      const message = text || `Request failed with status ${res.status}`;
      throw new Error(message);
    }

    // Try to parse JSON if present; backend may or may not return a body
    try {
      return await res.json();
    } catch (_) {
      return { ok: true };
    }
  } catch (error) {
    console.error("Creating bet failed:", error);
    throw error;
  }
}

export const cancel = async (betId: string): Promise<any> => {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/bets/${betId}/cancel`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const message = text || `Request failed with status ${res.status}`;
    throw new Error(message);
  }

  return res.json()
}

export const increase = async (betId: string, stakedPoints: number): Promise<any> => {
  const bet = await fetch(`${process.env.REACT_APP_BACKEND_URL}/bets/${betId}/increase`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({amount: stakedPoints})
  });
  if (!bet.ok) {
    const text = await bet.text().catch(() => '');
    const message = text || `Request failed with status ${bet.status}`;
    throw new Error(message);
  }
  return bet.json();
}

const unwrapApiEnvelope = <T>(payload: ApiEnvelope<T>): T => {
  if (payload && typeof payload === "object" && "data" in payload) {
    return payload.data as T;
  }

  return payload as T;
};

export const getMyBetHistory = async (params: BetHistoryQueryParams = {}): Promise<UserBetHistoryResponse> => {
  const response = await fetch(buildBetUrl("/users/me/bets", params), {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to load user bets: ${response.status} ${response.statusText} ${text}`);
  }

  return unwrapApiEnvelope<UserBetHistoryResponse>(await response.json());
};
